import type { Account } from "./api";
import { flagsOf, inRotation, type Section, STATUS_TEXT, weeksOf } from "./accounts";
import { earlyPullOf } from "./calendar";
import { type SourceFilter, sourceMatches } from "./dataSource";

export interface AccountFilter {
  q: string;
  status: Account["status"] | "";
  week: number;
  flagged: boolean;
  /** Design 9.6: the commercial status and the service status do not agree. */
  mismatch?: boolean;
  /** Design 8.5: projected above 100% less the margin at the next visit. */
  early?: boolean;
  /** Roadmap 020: a geocoded front door that nobody has checked yet. */
  pins?: boolean;
  /** A rough geocode of the front door: a person must check the pin. */
  rough?: boolean;
  /** Design 26.11 F4: the source tag of the account. */
  source?: SourceFilter;
  /** Rule V-20: an account with no service plan, thus no area. */
  noPlan?: boolean;
}

/** Pins to check or Rough pins: the page shows the pin list. */
export const pinView = (filter: AccountFilter) => Boolean(filter.pins || filter.rough);

export const NO_FILTER: AccountFilter = { q: "", status: "", week: 0, flagged: false, mismatch: false, early: false, pins: false, rough: false, source: "", noPlan: false };

export const isFiltered = (filter: AccountFilter) =>
  Boolean(filter.q.trim() || filter.status || filter.week || filter.flagged || filter.mismatch || filter.early || filter.pins || filter.rough || filter.source || filter.noPlan);

const compact = (text: string) => text.toLowerCase().replace(/[\s-]/g, "");

/** Each word of the search must be in the number, the name or the address. */
export function matchesQuery(account: Pick<Account, "number" | "name" | "address">, q: string): boolean {
  const text = `${account.number} ${account.name} ${account.address ?? ""}`.toLowerCase();
  const number = compact(account.number);
  return q
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => text.includes(word) || number.includes(compact(word)));
}

export function filterAccounts(accounts: Account[], filter: AccountFilter): Account[] {
  return accounts.filter((account) => {
    return (
      (!filter.status || account.status === filter.status) &&
      (!filter.week || (weeksOf(account).includes(filter.week) && (filter.status !== "" || inRotation(account)))) &&
      (!filter.flagged || flagsOf(account).length > 0) &&
      (!filter.mismatch || Boolean(account.mismatch)) &&
      (!filter.early || earlyPullOf(account)) &&
      (!filter.pins || account.door?.source === "geocode") &&
      (!filter.rough || Boolean(account.door?.check)) &&
      (!filter.noPlan || !account.plan) &&
      sourceMatches(account.data_source, filter.source ?? "") &&
      matchesQuery(account, filter.q)
    );
  });
}

/** With a rotation week chosen, show only the section of that week and the sections of other kinds. */
export const sectionsFor = (sections: Section[], filter: AccountFilter): Section[] =>
  filter.week ? sections.filter((s) => !s.key.startsWith("week-") || s.key === `week-${filter.week}`) : sections;

const SOURCES = ["sheet", "app", "scheduled"];

export function readFilter(search: string): AccountFilter {
  const params = new URLSearchParams(search);
  const status = params.get("status") ?? "";
  const week = Number(params.get("week"));
  const source = params.get("source") ?? "";
  return {
    q: params.get("q") ?? "",
    status: status in STATUS_TEXT ? (status as Account["status"]) : "",
    week: [1, 2, 3, 4].includes(week) ? week : 0,
    flagged: params.get("flagged") === "1",
    mismatch: params.get("mismatch") === "1",
    early: params.get("early") === "1",
    pins: params.get("pins") === "1",
    rough: params.get("rough") === "1",
    source: (SOURCES.includes(source) ? source : "") as SourceFilter,
    noPlan: params.get("noplan") === "1",
  };
}

export function writeFilter(filter: AccountFilter): string {
  const params = new URLSearchParams();
  if (filter.q.trim()) params.set("q", filter.q.trim());
  if (filter.status) params.set("status", filter.status);
  if (filter.week) params.set("week", String(filter.week));
  if (filter.flagged) params.set("flagged", "1");
  if (filter.mismatch) params.set("mismatch", "1");
  if (filter.early) params.set("early", "1");
  if (filter.pins) params.set("pins", "1");
  if (filter.rough) params.set("rough", "1");
  if (filter.source) params.set("source", filter.source);
  if (filter.noPlan) params.set("noplan", "1");
  const text = params.toString();
  return text ? `?${text}` : "";
}
